import React from "react";
import { useForm } from "react-hook-form";
import { theme } from "@src/theme";
import styled from "styled-components";
import Spinner from "@src/components/Spinner";
import { SubmitButton } from "@src/components/SubmitButton";
import { useUpdateInvoice } from "@src/queries/invoiceQueries";
import { useNotification } from "@src/context/NotificationContext";
import { IInvoice, InvoiceStatus } from "@src/types/invoice";

const ActionsButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50px;
  gap: ${theme.margins.xl};
  margin-top: ${theme.margins.xl};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.margins.medium};
  align-items: center;
  width: 100%;
`;

const SelectInput = styled.select`
  width: 100%;
  max-width: 300px;
  height: 48px;
  padding: 0 16px;
  border-radius: 8px;
  background-color: ${theme.colors.gray150};
  color: ${theme.colors.black};
  border: 1px solid transparent;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  text-align: center;
  text-align-last: center;

  &:focus {
    border-color: ${theme.colors.yellow};
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.gray};
  margin-bottom: 12px;
  display: block;
  text-align: center;
`;

interface EditInvoiceModalProps {
  invoice: IInvoice;
  onSuccess: () => void;
  onCancel: () => void;
}

const EditInvoiceModal = ({
  invoice,
  onSuccess,
  onCancel,
}: EditInvoiceModalProps): React.JSX.Element => {
  const updateInvoiceMutation = useUpdateInvoice();
  const { addNotification } = useNotification();

  const {
    handleSubmit,
    register,
    formState: {},
  } = useForm<{ invoiceStatus: InvoiceStatus }>({
    defaultValues: {
      invoiceStatus: invoice.invoiceStatus || "submitted",
    },
  });

  const onSubmit = (data: { invoiceStatus: InvoiceStatus }) => {
    updateInvoiceMutation.mutate(
      {
        documentId: invoice.documentId,
        payload: {
          invoiceStatus: data.invoiceStatus,
        },
      },
      {
        onSuccess: () => {
          addNotification("Invoice updated successfully", "success");
          onSuccess();
        },
        onError: () => {
          addNotification("Failed to update invoice", "error");
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ padding: "10px 20px" }}>
      <FormRow>
        <Label>Status</Label>
        <SelectInput {...register("invoiceStatus")}>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="cancelled">Cancelled</option>
        </SelectInput>
      </FormRow>

      <ActionsButtonGroup>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontWeight: "bold",
            color: theme.colors.gray,
            fontSize: "14px",
          }}
        >
          Cancel
        </button>
        <SubmitButton
          disabled={updateInvoiceMutation.isPending}
          type="submit"
          style={{ width: "120px" }}
        >
          {updateInvoiceMutation.isPending ? <Spinner small /> : "Save"}
        </SubmitButton>
      </ActionsButtonGroup>
    </form>
  );
};

export default EditInvoiceModal;
